// FUNCTIONALITY-115A — safe optional payment comment/link handling.
export const PAYMENT_COMMENT_MAX_LENGTH = 500;
export const PAYMENT_URL_MAX_LENGTH = 2048;

const ALLOWLIST = [
  "pay.monobank.ua",
  "send.monobank.ua",
  "privat24.ua",
  "next.privat24.ua",
  "checkout.novapay.ua",
  "novapay.ua",
];

export function sanitizePaymentComment(raw: string): string {
  return String(raw ?? "").normalize("NFKC").replace(/[\u0000-\u001F\u007F-\u009F]/g, "").trim();
}

const ABUSE = [
  /б[\W_]*л[\W_]*я[\W_]*д/iu,
  /х[\W_]*у[\W_]*[йи]/iu,
  /п[\W_]*и[\W_]*з[\W_]*д/iu,
  /[еёe][\W_]*б[\W_]*(?:а|у|и|л)/iu,
  /с[\W_]*у[\W_]*к[\W_]*а/iu,
  /f[\W_]*u[\W_]*c[\W_]*k/iu,
  /s[\W_]*h[\W_]*i[\W_]*t/iu,
  /b[\W_]*i[\W_]*t[\W_]*c[\W_]*h/iu,
];
const DANGEROUS = [
  /<\s*\/?\s*(?:script|iframe|object|embed|style|img|svg|a)\b/iu,
  /\bon\w+\s*=/iu,
  /\b(?:javascript|data|file|vbscript)\s*:/iu,
  /\b(?:ignore|disregard)\s+(?:all\s+)?(?:previous|system|developer)\b/iu,
  /\b(?:system|developer)\s+(?:prompt|message)\b/iu,
  /\b(?:drop|truncate|alter)\s+table\b/iu,
  /(?:--|;)\s*(?:select|insert|update|delete|drop)\b/iu,
];

export type PaymentTextValidation =
  | { ok: true; value: string }
  | { ok: false; reason: "too_long" | "control_obfuscation" | "html" | "abusive" | "instruction_like"; message: string };

export function validatePaymentComment(raw: string): PaymentTextValidation {
  const original = String(raw ?? "").normalize("NFKC");
  if (original.length > PAYMENT_COMMENT_MAX_LENGTH) {
    return { ok: false, reason: "too_long", message: "Коментар має містити не більше 500 символів." };
  }
  if (/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/u.test(original)) {
    return { ok: false, reason: "control_obfuscation", message: "Приберіть службові символи та спробуйте ще раз." };
  }
  const value = sanitizePaymentComment(raw);
  if (!value) return { ok: true, value: "" };
  if (ABUSE.some((pattern) => pattern.test(value))) {
    return { ok: false, reason: "abusive", message: "Будь ласка, перефразуйте коментар без образливих слів." };
  }
  if (/<[^>]*>/u.test(value) || /(?:\*\*|__|```)[\s\S]*(?:\*\*|__|```)/u.test(value)) {
    return { ok: false, reason: "html", message: "Використовуйте лише звичайний текст без HTML або розмітки." };
  }
  if (DANGEROUS.some((pattern) => pattern.test(value))) {
    return { ok: false, reason: "instruction_like", message: "Коментар схожий на небезпечну команду. Перефразуйте його." };
  }
  return { ok: true, value };
}

export function validatePaymentUrl(raw: string):
  | { ok: true; value: string; clickable: boolean }
  | { ok: false; reason: string; message: string } {
  const value = String(raw ?? "").normalize("NFKC").trim();
  if (!value) return { ok: true, value: "", clickable: false };
  if (value.length > PAYMENT_URL_MAX_LENGTH) {
    return { ok: false, reason: "too_long", message: "Payment URL is too long." };
  }
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return { ok: false, reason: "malformed", message: "Enter a valid HTTPS payment URL." };
  }
  if (url.protocol !== "https:" || !url.hostname) {
    return { ok: false, reason: "unsafe_scheme", message: "Payment URL must use HTTPS." };
  }
  return { ok: true, value: url.toString(), clickable: isAllowlistedPaymentUrl(url.toString()) };
}

export function isAllowlistedPaymentUrl(value: string): boolean {
  try {
    const url = new URL(value);
    if (url.protocol !== "https:") return false;
    return ALLOWLIST.some((host) => url.hostname === host || url.hostname.endsWith(`.${host}`));
  } catch {
    return false;
  }
}

export function formatPaymentCommentForManager(value: string): string {
  if (!value) return "—";
  if (isAllowlistedPaymentUrl(value)) return value;
  return value;
}
